import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { readScan, useRefreshConnections } from '@/lib/connections'
import { LinkAsk } from './LinkAsk'

// Scanning someone's connect code with the app itself, from People. The phone's own camera app would open the link in
// the browser, which on an iPhone isn't signed in to the home-screen app, so it lands on the front door instead.
// Here it reads the code, shows who it is, and asks; an invite to a circle opens its join screen.
export function Scan({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [token, setToken] = useState<string | null>(null)
  const navigate = useNavigate()
  const refresh = useRefreshConnections()
  function close() {
    onClose()
    setToken(null)
    void refresh()
  }
  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-sm">
        {token ? (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Connect</DialogTitle>
              <DialogDescription>Who this code is for, and asking to connect.</DialogDescription>
            </DialogHeader>
            <LinkAsk token={token} onDone={close} />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Scan their code</DialogTitle>
              <DialogDescription>
                Point your camera at their QR code. They’ll find it on People, under + and My QR code.
              </DialogDescription>
            </DialogHeader>
            {open && (
              <Camera
                onRead={(text) => {
                  const scanned = readScan(text, location.origin)
                  if (!scanned) return false
                  if (scanned.kind === 'connect') setToken(scanned.token)
                  else {
                    onClose()
                    navigate(`/join/${scanned.token}`)
                  }
                  return true
                }}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// How often a frame is read, in milliseconds: quick enough to feel instant, light enough for an older phone.
const EVERY = 120
// Frames are read at most this wide; QR codes held up to a phone are big, and smaller frames read faster.
const READ_WIDTH = 640

// The camera, reading every frame for a QR code until onRead takes one (returns true). A code that isn't ours says so
// quietly and scanning goes on.
function Camera({ onRead }: { onRead: (text: string) => boolean }) {
  const video = useRef<HTMLVideoElement>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [notOurs, setNotOurs] = useState(false)
  const read = useRef(onRead)
  useEffect(() => {
    read.current = onRead
  })

  useEffect(() => {
    let stream: MediaStream | null = null
    let timer = 0
    let stopped = false
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d', { willReadFrequently: true })

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) return setProblem('This browser can’t use the camera.')
      const [{ default: jsQR }, media] = await Promise.all([
        import('jsqr'),
        navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false }),
      ])
      stream = media
      if (stopped) return stream.getTracks().forEach((t) => t.stop())
      const el = video.current!
      el.srcObject = stream
      await el.play().catch(() => {})
      const tick = () => {
        if (stopped) return
        if (context && el.videoWidth) {
          const scale = Math.min(1, READ_WIDTH / el.videoWidth)
          canvas.width = Math.round(el.videoWidth * scale)
          canvas.height = Math.round(el.videoHeight * scale)
          context.drawImage(el, 0, 0, canvas.width, canvas.height)
          const frame = context.getImageData(0, 0, canvas.width, canvas.height)
          const code = jsQR(frame.data, frame.width, frame.height, { inversionAttempts: 'dontInvert' })
          if (code?.data) {
            if (read.current(code.data)) return
            setNotOurs(true)
          }
        }
        timer = window.setTimeout(tick, EVERY)
      }
      tick()
    }
    start().catch((e: unknown) => {
      const name = e instanceof DOMException ? e.name : ''
      setProblem(
        name === 'NotAllowedError'
          ? 'gatheredfrom isn’t allowed to use the camera. You can allow it in your phone’s settings, then try again.'
          : name === 'NotFoundError' || name === 'OverconstrainedError'
            ? 'There’s no camera here to scan with.'
            : 'The camera didn’t start. Close this and try again.',
      )
    })
    return () => {
      stopped = true
      window.clearTimeout(timer)
      stream?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  if (problem)
    return (
      <p role="alert" className="text-balance text-muted-foreground">
        {problem}
      </p>
    )
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="scan-window relative aspect-square w-full overflow-hidden rounded-2xl bg-black">
        <video
          ref={video}
          playsInline
          muted
          autoPlay
          aria-label="Camera"
          className="absolute inset-0 size-full object-cover"
        />
        <span aria-hidden className="scan-frame" />
      </div>
      <p className="min-h-5 text-sub text-muted-foreground" role="status">
        {notOurs ? 'That code isn’t a gatheredfrom link.' : ''}
      </p>
    </div>
  )
}
